import { z } from 'zod';
import { BASE_STAT_TOTAL, CREATURES, statTotal } from './creatures.ts';
import { ELEMENT_TYPES, RESISTED_BY, SUPER_EFFECTIVE } from './types.ts';

const elementType = z.enum(ELEMENT_TYPES);
const stat = z.number().int().min(20).max(200);

export const creatureSchema = z.object({
  id: z.string().regex(/^[a-z][a-z0-9_]*$/),
  name: z.string().min(2),
  types: z.union([z.tuple([elementType]), z.tuple([elementType, elementType])]),
  archetype: z.enum(['quadruped', 'biped', 'avian', 'serpentine', 'floater']),
  role: z.string().min(1),
  concept: z.string().min(1),
  stats: z.object({ vit: stat, str: stat, foc: stat, grd: stat, res: stat, agi: stat }),
  slice: z.boolean(),
});

/** Validates all content. Throws with every problem listed. Run in tests and at build time. */
export function validateContent(): void {
  const problems: string[] = [];

  for (const t of ELEMENT_TYPES) {
    const overlap = SUPER_EFFECTIVE[t].filter((d) => RESISTED_BY[t].includes(d));
    if (overlap.length) problems.push(`type ${t}: both super effective and resisted vs ${overlap}`);
  }

  const ids = new Set<string>();
  for (const c of CREATURES) {
    const parsed = creatureSchema.safeParse(c);
    if (!parsed.success) problems.push(`creature ${c.id}: ${parsed.error.message}`);
    if (ids.has(c.id)) problems.push(`creature ${c.id}: duplicate id`);
    ids.add(c.id);
    if (statTotal(c.stats) !== BASE_STAT_TOTAL) {
      problems.push(`creature ${c.id}: stat total ${statTotal(c.stats)} != ${BASE_STAT_TOTAL}`);
    }
    if (c.types.length === 2 && c.types[0] === c.types[1]) {
      problems.push(`creature ${c.id}: duplicate type`);
    }
  }

  if (problems.length) throw new Error(`Invalid content:\n- ${problems.join('\n- ')}`);
}
